import moment from 'moment';
import config from 'src/shared/config';
import axios from 'src/shared/utils/axios';
import { toLocalDateTimeStr } from 'src/shared/utils/localDateTime';
import {
  ApprovalDetailDto,
  ApprovalDto,
  ApprovalType,
  DetectedFileInfo,
  DownloadHistoryInfo,
  FileDetailsInfo,
  FileInfoDto,
} from './approvalApi';
import { ApplyGenKeyDto } from './decryptApprovalApi';
const FXM_API_URL = config.servers.fxm.url;
export enum PrintFileAllowedScope {
  PERIOD = 'PERIOD',
  COUNT = 'COUNT',
}
export interface PrintFileApprovalDto extends ApprovalDto {
  approvalNo: string;
  submission: string;
  limitCount: number;
  allowedScope: PrintFileAllowedScope;
  watermarkPrint: boolean; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: boolean; // 적용 범위 > 민감 정보 (Y/N)
}
export interface PrintFileApprovalReqDto extends ApprovalDto {
  approvalNo: string;
  submission: string;
  startDate: number; // 요청 시작 시간
  endDate: number; // 요청 종료 시간
  watermarkPrint: boolean; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: boolean; // 적용 범위 > 민감 정보 (Y/N)
  allowedScope: string;
  limitCount: number;
}
export interface PrintFileApprovalServerReqDto extends ApprovalDto {
  approvalNo: string;
  submission: string;
  startDate: number; // 요청 시작 시간
  endDate: number; // 요청 종료 시간
  watermarkPrint: string; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: string; // 적용 범위 > 민감 정보 (Y/N)
  allowedScope: string;
  limitCount: number;
}
export interface PrintFileApprovalReqEntity extends PrintFileApprovalReqDto {
  startDateStr: string;
  endDateStr: string;
}
export const defaultPrintFileApprovalReqEntity: PrintFileApprovalReqEntity = {
  approvalNo: '',
  submission: '',
  code: '',
  approvalStep: 1, // 신청 단계(정책에 따라 1~N까지 나뉨, 자가 승인일 경우 1)
  currentStep: 1, //신청 시 단계, 1 고정
  approvalType: ApprovalType.PRINTFILE,
  status: '',
  approvalMethod: '',
  remark: '',
  approvers: [],
  startDate: moment().startOf('d').valueOf(),
  endDate: moment().endOf('d').valueOf(),
  watermarkPrint: false, // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: false, // 적용 범위 > 민감 정보 (Y/N)
  allowedScope: 'PERIOD',
  limitCount: 1,
  startDateStr: '',
  endDateStr: '',
};
const convertPrintFileReqDtoToServerDto = (
  reqDto: PrintFileApprovalReqDto
): PrintFileApprovalServerReqDto => {
  return {
    approvalNo: reqDto.approvalNo,
    submission: reqDto.submission,
    code: reqDto.code,
    approvalStep: reqDto.approvalStep,
    currentStep: reqDto.currentStep,
    approvalType: reqDto.approvalType,
    status: reqDto.status,
    approvalMethod: reqDto.approvalMethod,
    remark: reqDto.remark,
    approvers: reqDto.approvers,
    startDate: reqDto.startDate,
    endDate: reqDto.endDate,
    watermarkPrint: reqDto.watermarkPrint ? 'Y' : 'N',
    detectionPrint: reqDto.detectionPrint ? 'Y' : 'N',
    allowedScope: reqDto.allowedScope.valueOf(),
    limitCount: reqDto.limitCount,
  };
};
export interface PrintFileApprovalDetailDto extends ApprovalDetailDto {
  startDate: number;
  endDate: number;
  printPolStatus: string; // 기간 내 예외 상태
  watermarkPrint: string; // 적용 범위 > 워터마크 예외(Y/N)
  detectionPrint: string; // 적용 범위 > 민감 정보 (Y/N)
  limitCount: number; // 인쇄 횟수
  submission: string; //파일 입력 제출처
  allowedScope: PrintFileAllowedScope; // 사용 유형 ( PERIOD: 기간 / COUNT: 사용 횟수)
  //allowedScope
  //    - PERIOD : startDate & endDate 를 사용
  //    - COUNT  : limitCount 사용

  fileDetails: FileDetailsInfo[];
  downloadHistory: DownloadHistoryInfo[];
  detectedFiles?: DetectedFileInfo[];
}
export interface PrintFileApprovalDetailEntity extends PrintFileApprovalDetailDto {
  startDateStr: string;
  endDateStr: string;
  applyDateStr: string;
  updatedTsStr: string;
}
const convertPrintFileDetailDtoToEntity = (
  dto: PrintFileApprovalDetailDto
): PrintFileApprovalDetailEntity => {
  return {
    ...dto,
    startDateStr: dto.startDate > 0 ? toLocalDateTimeStr(dto.startDate) : '',
    endDateStr: dto.endDate > 0 ? toLocalDateTimeStr(dto.endDate) : '',
    applyDateStr: dto.applyDate > 0 ? toLocalDateTimeStr(dto.applyDate) : '',
    updatedTsStr: dto.updatedTs > 0 ? toLocalDateTimeStr(dto.updatedTs) : '',
  };
};
interface PrintFileApprovalApi {
  find(code: string): Promise<PrintFileApprovalDetailEntity>;
  //find2는 내 신청함 > 결재 완료 페이지에서 호출
  find2(code: string): Promise<PrintFileApprovalDetailEntity>;

  getGenKey(): Promise<ApplyGenKeyDto>;
  fileUpload(
    approvalNo: string,
    code: string,
    files: File[],
    progressConfig: {
      onUploadProgress: (progressEvent: any) => void;
    }
  ): Promise<FileInfoDto[]>;

  deleteUploadFile(approvalNo: string, fileUuid: string): Promise<FileInfoDto>;
  deleteUploadFiles(approvalNo: string, fileUuids: string[]): Promise<FileInfoDto[]>;

  apply(reqDto: PrintFileApprovalReqDto): Promise<PrintFileApprovalDetailEntity>;
  cancel(approvalNo: string): Promise<boolean>;
  carryout(approvalNo: string): Promise<boolean>;
}
class PrintFileApprovalServerApi implements PrintFileApprovalApi {
  async find(code: string): Promise<PrintFileApprovalDetailEntity> {
    const resp = await axios.get<PrintFileApprovalDetailDto>(
      `${FXM_API_URL}/api/printfile/find/${code}`
    );
    return convertPrintFileDetailDtoToEntity(resp.data);
  }
  async find2(code: string): Promise<PrintFileApprovalDetailEntity> {
    const resp = await axios.get<PrintFileApprovalDetailDto>(
      `${FXM_API_URL}/api/printfile/find2/${code}`
    );
    return convertPrintFileDetailDtoToEntity(resp.data);
  }

  async getGenKey(): Promise<ApplyGenKeyDto> {
    const resp = await axios.get<ApplyGenKeyDto>(`${FXM_API_URL}/api/genkey`);
    return resp.data;
  }

  async fileUpload(
    approvalNo: string,
    code: string,
    files: File[],
    progressConfig: {
      onUploadProgress: (progressEvent: any) => void;
    }
  ): Promise<FileInfoDto[]> {
    const form = new FormData();
    form.append('approvalType', 'PRINTFILE');
    form.append('approvalNo', approvalNo);
    form.append('code', code);
    files.map((file) => form.append('files', file));

    const resp = await axios.post<FileInfoDto[]>(
      `${FXM_API_URL}/api/printfile/upload`,
      form,
      progressConfig
    );

    return resp.data;
  }

  async deleteUploadFile(approvalNo: string, fileUuid: string): Promise<FileInfoDto> {
    const resp = await axios.get<FileInfoDto>(
      `${FXM_API_URL}/api/printfile/delete/one/${approvalNo}/${fileUuid}`
    );
    return resp.data;
  }
  async deleteUploadFiles(approvalNo: string, fileUuids: string[]): Promise<FileInfoDto[]> {
    const resp = await axios.post<FileInfoDto[]>(
      `${FXM_API_URL}/api/printfile/delete/many/${approvalNo}`,
      fileUuids
    );
    return resp.data;
  }

  async apply(reqDto: PrintFileApprovalReqDto): Promise<PrintFileApprovalDetailEntity> {
    const serverDto = convertPrintFileReqDtoToServerDto(reqDto);
    const resp = await axios.post<PrintFileApprovalDetailDto>(
      `${FXM_API_URL}/api/printfile/apply`,
      serverDto
    );
    return convertPrintFileDetailDtoToEntity(resp.data);
  }
  async cancel(approvalNo: string): Promise<boolean> {
    const resp = await axios.get<PrintFileApprovalDetailDto>(
      `${FXM_API_URL}/api/printfile/cancel/${approvalNo}`
    );
    if ([400, 500].includes(resp.status)) {
      return false;
    }
    return true;
  }
  async carryout(approvalNo: string): Promise<boolean> {
    const resp = await axios.get<PrintFileApprovalDetailDto>(
      `${FXM_API_URL}/api/printfile/carryout/${approvalNo}`
    );
    if ([400, 500].includes(resp.status)) {
      return false;
    }
    return true;
  }
}
const printFileApprovalApi = new PrintFileApprovalServerApi();
export default printFileApprovalApi;
